import { useSettlementToken } from "features/tokens/useSettlementToken";
import React, { useEffect } from "react";
import Button from "components/button/Button";
import LoadingModal from "modals/loading/LoadingModal";
import ErrorModal from "modals/error-modal/ErrorModal";
import { useAccountFetcher } from "./useAccountFetcher";
import type { AccountWithBalance } from "./useAccountFetcher";
import type { Network } from "@gardio/contracts/polygon";
import "./account-picker.css";

interface AccountCardProps {
  account: AccountWithBalance;
  index: number;
  onSelect: () => void;
}

function AccountCard({ account, index, onSelect }: AccountCardProps) {
  const { label: tokenSymbol } = useSettlementToken();
  const hasBalance =
    account.balance !== undefined && !isNaN(account.balance);

  return (
    <button className="tu-account-card" onClick={onSelect} type="button">
      <div className="tu-account-card__top">
        <span className="tu-account-card__idx">Account {index + 1}</span>
        <span className="tu-account-card__path">{account.path}</span>
      </div>
      <div className="tu-account-card__addr">{account.address}</div>
      <div className="tu-account-card__balance">
        {hasBalance ? (
          <>
            <span className="tu-account-card__bal-num">
              {account.balance!.toLocaleString("en", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}
            </span>
            <span className="tu-account-card__bal-unit"> {tokenSymbol}</span>
          </>
        ) : (
          <span className="tu-account-card__bal-na">Balance unavailable</span>
        )}
      </div>
    </button>
  );
}

interface Props {
  network: Network;
  onSelect: (account: AccountWithBalance) => void;
  onBack: () => void;
}

function AccountPicker({ network, onSelect, onBack }: Props) {
  const { accounts, loading, error, hasMore, fetchBatch, clearError } =
    useAccountFetcher(network);

    console.log(`accounts: `, accounts);
  useEffect(() => {
    fetchBatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showInitialLoading = loading && accounts.length === 0;

  return (
    <>
      <div className="tu-account-picker">
        <div className="tu-picker-header">
          <h3 className="v1-modal-title">Select Account</h3>
          <p className="v1-modal-subtitle">
            Choose the hardware wallet account to send from.
          </p>
        </div>

        {accounts.length === 0 && !loading ? (
          <div className="tu-picker-empty">
            No accounts found. Connect your Keyfob and try again.
          </div>
        ) : (
          <div className="tu-picker-list">
            {accounts.map((acc, i) => (
              <AccountCard
                key={acc.address + i}
                account={acc}
                index={i}
                onSelect={() => onSelect(acc)}
              />
            ))}

            {loading && accounts.length > 0 && (
              <div className="tu-picker-loading">
                <span className="tu-spinner" />
                Loading accounts…
              </div>
            )}
          </div>
        )}

        {hasMore && accounts.length > 0 && !loading && (
          <Button variant="subtle" size="sm" onClick={fetchBatch}>
            Load more
          </Button>
        )}

        <div className="v1-modal-actions">
          <Button variant="ghost" size="md" onClick={onBack}>
            Back
          </Button>
        </div>
      </div>

      <LoadingModal
        isOpen={showInitialLoading}
        loadingMsg="Fetching accounts from device…"
      />

      <ErrorModal
        isOpen={!!error}
        errorMsg={error ?? ""}
        handleClose={clearError}
        tryAgain={() => {
          clearError();
          fetchBatch();
        }}
      />
    </>
  );
}

export default AccountPicker;
